import React from "react";

import MuiIconButton, {
  IconButtonProps as MuiIconButtonProps,
} from "@mui/material/IconButton";
import { Theme } from "@mui/material/styles";
import styled from "@mui/material/styles/styled";

export type IconButtonProps = MuiIconButtonProps & {
  variant?: "outlined" | "text" | "contained";
  round?: boolean;
  colorVariant?: keyof Omit<Theme["palette"]["button"], "disabled">;
};

const StyledButton = styled<React.FC<IconButtonProps>>(MuiIconButton, {
  shouldForwardProp: (propName) =>
    propName !== "variant" &&
    propName !== "round" &&
    propName !== "colorVariant",
})(({ round, theme, variant, colorVariant = "primary" }) => ({
  borderRadius: round ? theme.numbers.corner.round : theme.numbers.corner.small,
  transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  ...(variant === "outlined" && {
    border: "1px solid",
    borderColor: theme.palette.outline.default,
  }),
  "& .MuiSvgIcon-root": {
    color:
      variant === "contained"
        ? theme.palette.button[colorVariant].icon
        : theme.palette.icon.weak,
    transition: "color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  },
  ...(variant === "contained" && {
    backgroundColor: theme.palette.button[colorVariant].bg.default,
  }),
  "&:hover, &.focused": {
    color:
      variant === "contained"
        ? theme.palette.button[colorVariant].icon
        : theme.palette.button.secondary.text,
    backgroundColor:
      variant === "contained"
        ? theme.palette.button[colorVariant].bg.hover
        : theme.palette.button.secondary.bg.hover,
    ...(variant === "outlined" && {
      borderColor: theme.palette.button.secondary.outline,
    }),
    "& .MuiSvgIcon-root": {
      color:
        variant === "contained"
          ? theme.palette.button[colorVariant].icon
          : theme.palette.button.secondary.icon,
    },
  },
  "&.Mui-disabled": {
    border: "none",
    backgroundColor: theme.palette.button.disabled.bg.disabled,
    "& .MuiSvgIcon-root": {
      color: theme.palette.button.disabled.icon,
    },
  },
}));

const IconButton = ({ variant = "text", ...props }: IconButtonProps) => {
  return <StyledButton disableRipple variant={variant} {...props} />;
};

export default IconButton;
